// ===== AI SOLUTIONS PAGE DATA =====

export const aiHeroData = {
  badge: "AI Integration \u2022 Automation \u2022 Intelligent Products",
  heading: "AI Solutions Built for Real Business Impact",
  highlight: "Real Business Impact",
  subtext:
    "From AI-powered chat assistants and document intelligence to workflow automation and predictive analytics, we help you bring modern AI capabilities into your applications, SaaS products and business operations.",
  primaryCTA: "Start Your AI Project",
  secondaryCTA: "Explore AI Services",
  statCards: [
    { value: "40%+", label: "Average efficiency gains" },
    { value: "50+", label: "AI features shipped" },
    { value: "15+", label: "AI models integrated" },
    { value: "24/7", label: "Automated operations" },
  ],
  image:
    "https://readdy.ai/api/search-image?query=Abstract%20AI%20artificial%20intelligence%20hero%20visual%2C%20neural%20network%20nodes%20and%20glowing%20connections%20in%20deep%20navy%20and%20purple%20with%20orange%20highlights%2C%20floating%203D%20geometric%20shapes%2C%20data%20particles%2C%20modern%20cloud%20architecture%2C%20premium%20tech%20aesthetic%2C%20gradient%20lighting%2C%20dark%20background%2C%20no%20text&width=1400&height=900&seq=astrojal-ai-hero&orientation=landscape",
};

export const aiServicesData = {
  heading: "AI Services We Deliver",
  subtext:
    "End-to-end AI engineering — from strategy and model integration to production deployment, built on top of Laravel and modern cloud infrastructure.",
  items: [
    {
      title: "AI Chat & Copilots",
      desc: "Custom AI assistants and copilots embedded in your product, trained on your data to answer questions, guide users and automate support.",
      icon: "ri-chat-smile-3-line",
      gradient: "from-[#2563EB] to-[#7C3AED]",
      points: ["Brand-aware assistants", "Document-grounded answers", "Human handoff flows"],
    },
    {
      title: "Document Intelligence",
      desc: "Extract, classify and summarize data from PDFs, contracts, invoices and unstructured documents automatically.",
      icon: "ri-file-search-line",
      gradient: "from-[#7C3AED] to-[#F97316]",
      points: ["OCR & parsing", "Contract analysis", "Automated data entry"],
    },
    {
      title: "Intelligent Search & RAG",
      desc: "Semantic search and retrieval-augmented generation that lets users find answers inside your own knowledge base.",
      icon: "ri-search-eye-line",
      gradient: "from-[#F97316] to-[#F59E0B]",
      points: ["Vector search", "Knowledge grounding", "Accurate citations"],
    },
    {
      title: "Predictive Analytics",
      desc: "Forecast demand, churn, revenue and behavior using machine learning models connected to your business data.",
      icon: "ri-line-chart-line",
      gradient: "from-[#2563EB] to-[#F97316]",
      points: ["Demand forecasting", "Churn prediction", "Real-time dashboards"],
    },
    {
      title: "Workflow Automation",
      desc: "Automate repetitive business processes with AI agents, approvals, notifications and intelligent routing.",
      icon: "ri-robot-line",
      gradient: "from-[#7C3AED] to-[#2563EB]",
      points: ["AI agents", "Approval flows", "API integrations"],
    },
    {
      title: "AI Content & Generation",
      desc: "Generate on-brand copy, product descriptions, summaries and personalized content at scale.",
      icon: "ri-magic-line",
      gradient: "from-[#F59E0B] to-[#F97316]",
      points: ["On-brand tone", "Multi-language", "Human review loops"],
    },
    {
      title: "Image & Vision AI",
      desc: "Detect objects, moderate content, analyze images and build computer-vision features into your products.",
      icon: "ri-eye-line",
      gradient: "from-[#2563EB] to-[#F59E0B]",
      points: ["Object detection", "Content moderation", "Image analysis"],
    },
    {
      title: "AI Voice & Assistants",
      desc: "Voice-enabled interfaces, transcription and AI call handling for customer service and operations.",
      icon: "ri-mic-line",
      gradient: "from-[#F97316] to-[#7C3AED]",
      points: ["Speech to text", "Voice agents", "Multilingual support"],
    },
  ],
};

export const aiProcessData = {
  heading: "How We Build AI Features",
  subtext:
    "A proven, iterative process that turns AI concepts into reliable, production-ready features.",
  steps: [
    { number: "01", title: "Discovery & Use Case", desc: "We identify the highest-value AI opportunity for your business and define clear success metrics." },
    { number: "02", title: "Data & Strategy", desc: "We assess your data, choose the right model and design the integration architecture." },
    { number: "03", title: "Prototype & Validate", desc: "We build a working prototype fast, validate accuracy and refine the experience." },
    { number: "04", title: "Integrate & Build", desc: "We engineer the feature into your Laravel app with robust APIs, caching and security." },
    { number: "05", title: "Test & Optimize", desc: "We test for accuracy, cost, latency and safety, then optimize prompts and models." },
    { number: "06", title: "Deploy & Monitor", desc: "We deploy with monitoring, feedback loops and continuous improvement." },
  ],
};

export const aiStackData = {
  heading: "AI Technologies & Tools We Use",
  subtext:
    "We work with leading AI models, frameworks and infrastructure to build reliable, cost-efficient AI features.",
  groups: [
    {
      name: "LLM Models",
      icon: "ri-brain-line",
      items: [
        { name: "OpenAI", logo: "https://registry.npmmirror.com/@lobehub/icons-static-svg/latest/files/icons/openai.svg" },
        { name: "Claude", logo: "https://registry.npmmirror.com/@lobehub/icons-static-svg/latest/files/icons/claude-color.svg" },
        { name: "Gemini", logo: "https://registry.npmmirror.com/@lobehub/icons-static-svg/latest/files/icons/gemini-color.svg" },
        { name: "Microsoft Copilot", logo: "https://registry.npmmirror.com/@lobehub/icons-static-svg/latest/files/icons/copilot-color.svg" },
      ],
    },
    {
      name: "Vector & Search",
      icon: "ri-database-2-line",
      items: [
        { name: "Pinecone", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/pinecone/pinecone-original.svg" },
        { name: "Postgres", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/postgresql/postgresql-original.svg" },
        { name: "Redis", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/redis/redis-original.svg" },
        { name: "Meilisearch", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/meilisearch/meilisearch-original.svg" },
      ],
    },
    {
      name: "Frameworks",
      icon: "ri-code-s-slash-line",
      items: [
        { name: "Laravel", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/laravel/laravel-original.svg" },
        { name: "LangChain", logo: "https://registry.npmmirror.com/@lobehub/icons-static-svg/latest/files/icons/langchain.svg" },
        { name: "OpenAI SDK", logo: "https://registry.npmmirror.com/@lobehub/icons-static-svg/latest/files/icons/openai.svg" },
        { name: "Python", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg" },
      ],
    },
    {
      name: "Infrastructure",
      icon: "ri-cloud-line",
      items: [
        { name: "AWS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-original-wordmark.svg" },
        { name: "Docker", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/docker/docker-original.svg" },
        { name: "Cloudflare", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/cloudflare/cloudflare-original.svg" },
        { name: "Node.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nodejs/nodejs-original.svg" },
      ],
    },
  ],
};

export const aiUseCasesData = {
  heading: "AI in Action Across Industries",
  subtext:
    "Practical AI applications we build for businesses across sectors — ready to adapt to your needs.",
  items: [
    { title: "Customer Support Copilot", industry: "SaaS & Enterprise", desc: "Automate first-line support with an AI assistant grounded in your knowledge base and docs.", icon: "ri-customer-service-2-line" },
    { title: "Lead Qualification Agent", industry: "Sales & Marketing", desc: "Automatically score, qualify and route leads with intelligent conversational agents.", icon: "ri-user-search-line" },
    { title: "Document & Contract Review", industry: "Legal & Finance", desc: "Extract clauses, flag risks and summarize contracts in seconds instead of days.", icon: "ri-contract-line" },
    { title: "Product Recommendations", industry: "E-commerce", desc: "Personalized recommendations and dynamic pricing based on browsing and purchase data.", icon: "ri-shopping-cart-line" },
    { title: "Patient Intake Assistant", industry: "Healthcare", desc: "Automate patient intake, triage and scheduling with a HIPAA-conscious AI workflow.", icon: "ri-heart-pulse-line" },
    { title: "Knowledge Base Search", industry: "Enterprise", desc: "Give every employee instant, cited answers from internal documents and wikis.", icon: "ri-git-repository-line" },
  ],
};

export const aiLaravelData = {
  heading: "Why AstroJal for AI Development",
  subtext:
    "We combine deep Laravel engineering with modern AI integration to build intelligent products that are secure, scalable and cost-efficient.",
  items: [
    { title: "Laravel-First Integration", desc: "AI features built natively into your Laravel backend — queues, caching, jobs and APIs handled properly.", icon: "ri-code-box-line" },
    { title: "Security & Data Privacy", desc: "Careful handling of sensitive data, API keys stored securely, and role-based access controls.", icon: "ri-shield-check-line" },
    { title: "Cost-Optimized AI", desc: "Intelligent caching, model routing and token management to keep AI costs predictable.", icon: "ri-money-dollar-circle-line" },
    { title: "Reliable & Production-Ready", desc: "Monitoring, fallbacks and error handling so AI features are dependable at scale.", icon: "ri-server-line" },
  ],
};

export const aiFAQData = {
  heading: "AI Solutions FAQs",
  items: [
    {
      q: "How do you integrate AI into an existing Laravel application?",
      a: "We add AI as a well-structured service layer inside your Laravel app. We create dedicated classes for model calls, use queues for async processing, cache responses, and expose clean APIs to your frontend. This keeps AI features maintainable and easy to swap or upgrade.",
    },
    {
      q: "Which AI models do you work with?",
      a: "We work with OpenAI, Claude, Gemini and other leading models. We'll recommend the right model for your use case and budget, and we architect the integration so you can switch models without rewriting your code.",
    },
    {
      q: "Can you train AI on our company data?",
      a: "For most use cases we use retrieval-augmented generation (RAG) — which grounds answers in your documents without retraining a model. This is faster, cheaper and easier to keep up to date. We can also build custom fine-tuned solutions where it genuinely makes sense.",
    },
    {
      q: "How do you keep AI features cost-efficient?",
      a: "We use caching, semantic search over retrieval, token budget controls, model routing and batch processing. We monitor usage closely and set up alerts so costs stay predictable as usage grows.",
    },
    {
      q: "Is our data safe when using AI?",
      a: "Yes. We treat data privacy as a top priority. API keys are stored securely, we follow data residency requirements, and we design systems with role-based access, audit logs and careful handling of sensitive information.",
    },
    {
      q: "How long does it take to add an AI feature?",
      a: "A focused AI prototype can be ready in 2–4 weeks. Full production features typically take 4–8 weeks depending on complexity. We work iteratively so you see value quickly.",
    },
  ],
};